import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";
import { Stack } from "@mui/system";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  addPromoCode,
  getAllActiveProviders,
  getAllDoctorsAndPromoCodeTypes,
  updatePromoCode,
} from "../../../Api/Signin/Transaction.Api";
import { Theme } from "../../../Style/Theme";
import { getProviderDisplayName } from "../../../Utils/WordFormatter/ProviderAlias";
import useLoadingState from "../../../Utils/Hooks/useLodingState";
import useOverlayState from "../../../Utils/Hooks/useOverlayState";
import Calender from "../../atoms/Calender/CalenderDateRange/Calender";
import { SideDrawer } from "../../atoms/Drawer/SideDrawer";
import { CheckBoxInput } from "../../atoms/Input/CheckBoxInput";
import { NumberInputType3 } from "../../atoms/Input/NumberInput";
import { RemoteSelect } from "../../atoms/Input/RemoteSelect";
import { RemoteSelectMultiple } from "../../atoms/Input/RemoteSelectMultiple";
import { TextInput } from "../../atoms/Input/TextInput";
import { LoadingButtonType } from "../../atoms/LoadingButton/LoadingButton";
import { FormItemsWrap } from "../../molecules/Wrap/Wrap";
import { schema } from "./shema";

export const PromoCodeDrawer = ({
  close,
  data,
  OpenDrawer,
  isUpdate,
  refetch,
}) => {
  // ------------------------------------------------ Start => Deifinitions/ inits --------------------------------------------------

  const { t } = useTranslation();
  const [promoCodeTypes, setPromoCodeTypes] = useState();
  const [doctors, setDoctors] = useState();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const { OverlayState, openOverlay } = useOverlayState();
  const { LoadingState, startLoading, stopLoading } = useLoadingState();

  const {
    control,
    trigger,
    watch,
    reset,
    setError,
    setValue,
    getValues,
    handleSubmit,
  } = useForm({
    defaultValues: {
      promoCode: "",
      providerId: "",
      isGeneral: false,
      doctorIds: [],
      promoCodeType: "",
      value: "",
      dateRange: [],
      reusability: false,
    },
    resolver: yupResolver(schema(t)),
    criteriaMode: "all",
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // ------------------------------------------------ End => Deifinitions/ inits ------------------------------------------------------

  // ---------------------------------------------- Start => Action functions ---------------------------------------------------------

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const fetchedProviders = await GetProvidersList();
        const promoTypes = await GetDoctorsAndPromoCodeTypes();
        setSelectedRowData(promoTypes, fetchedProviders);
      } catch (err) {
        console.error("Error initializing PromoCodeDrawer:", err);
      } finally {
        setLoading(false);
        openOverlay({ OverlayName: "add" });
      }
    })();
  }, []);

  const GetProvidersList = async () => {
    const universalOption = { id: "universal", providerName: "Universal" };
    try {
      const response = await getAllActiveProviders();
      let providerOptions = [universalOption];
      if (response && response.type === 0 && Array.isArray(response.data)) {
        const mappedData = response.data.map((item) => ({
          id: item.id,
          providerName: getProviderDisplayName(item.providerName?.trim() || ""),
        }));
        providerOptions = [universalOption, ...mappedData];
      }
      setProviders(providerOptions);
      return providerOptions;
    } catch (e) {
      console.error("Error fetching active providers:", e);
      setProviders([universalOption]);
      return [universalOption];
    }
  };

  const setSelectedRowData = async (promoTypes, providerOptions) => {
    if (data && isUpdate) {
      await setSelectedPromoCodeType(data.type, promoTypes);
      const provId = data.providerId ? data.providerId : "universal";
      setValue("providerId", provId);
      setValue("isGeneral", data.isGeneral ?? false);
      await setSelectedDoctors(data.doctors);
      setValue("promoCode", data.code);
      setValue("value", data.value);
      setValue("reusability", data.reusability);
      await setDates();
    } else {
      reset();
      // Default selection to "First Doctor" provider
      const firstDoctorProv = providerOptions?.find(
        (p) => p.providerName === "Firstdoctor",
      );
      if (firstDoctorProv) {
        setValue("providerId", firstDoctorProv.id);
      } else {
        setValue("providerId", "universal");
      }
    }
  };

  const setDates = async () => {
    let temp = [];
    temp.push(new Date(data?.startDate));
    temp.push(new Date(data?.expirationDate));
    setValue("dateRange", temp);
  };

  const formatDate = (date) => {
    const convertedDate = new Date(date);
    const year = convertedDate.getFullYear();
    const month = String(convertedDate.getMonth() + 1).padStart(2, "0");
    const day = String(convertedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  };

  const setSelectedPromoCodeType = async (promoTypeName, promoTypes) => {
    promoTypes.map((promoCodeType) => {
      if (promoCodeType.name == promoTypeName) {
        setValue("promoCodeType", promoCodeType.id);
      }
    });
  };

  const setSelectedDoctors = async (doctors) => {
    let doctorIdList = [];
    if (doctors) {
      doctors.map((doctor) => {
        doctorIdList.push(doctor.Id.toLowerCase());
      });
    }
    setValue("doctorIds", doctorIdList);
  };

  const handleOnSelectDeselectAll = (doctorIds) => {
    setValue("doctorIds", doctorIds);
  };

  // Get all doctors & promo code types
  const GetDoctorsAndPromoCodeTypes = async () => {
    try {
      let response = await getAllDoctorsAndPromoCodeTypes();
      if (response.type === 0) {
        setDoctors(response.data.doctors);
        setPromoCodeTypes(
          response?.data?.promoCodeTypes?.map((i) => ({
            ...i,
            name: t(`finance-add-promo-code.${i?.name?.toLowerCase()}`, {
              ns: "glossary",
            }),
          })),
        );
        return response.data.promoCodeTypes;
      }
    } catch (exception) {}
  };

  const selectedProviderId = watch("providerId");
  const selectedProviderObj = providers.find(
    (p) => p.id === selectedProviderId,
  );
  const isFirstDoctorSelected =
    selectedProviderObj?.providerName === "Firstdoctor";
  const isUniversalSelected =
    selectedProviderId === "universal" || !selectedProviderId;

  // Add/edit promo code
  const submitHandler = async () => {
    try {
      startLoading({ btnName: "add" });
      const isValid = await trigger();
      const formValues = watch();
      const currentProv = providers.find((p) => p.id === formValues.providerId);
      const isUniv =
        formValues.providerId === "universal" || !formValues.providerId;
      const isFirstDoc = currentProv?.providerName === "Firstdoctor";

      if (isFirstDoc && (!formValues.doctorIds || formValues.doctorIds.length === 0)) {
        setError("doctorIds", {
          message: t("validation:required-field"),
        });
        stopLoading({ btnName: "add" });
        return;
      }

      if (!isValid) {
        stopLoading({ btnName: "add" });
        return;
      }

      let response;
      let body = {
        code: formValues.promoCode.trim(),
        providerId: isUniv ? null : formValues.providerId,
        isGeneral: isUniv,
        doctors: isFirstDoc ? formValues.doctorIds : [],
        type: formValues.promoCodeType,
        value: formValues.value,
        startDate: formatDate(formValues.dateRange[0]),
        expirationDate: formatDate(formValues.dateRange[1]),
        isReusable: isFirstDoc ? formValues.reusability : false,
      };

      if (isUpdate && data.id) {
        response = await updatePromoCode(data.id, body);
      } else {
        response = await addPromoCode(body);
      }

      if (response.type === 0) {
        reset();
        close();
        refetch();
      } else {
        setError(response.data.field, {
          message: t(response.message, { ns: "validation" }),
        });
      }
      stopLoading({ btnName: "add" });
    } catch (exception) {
      stopLoading({ btnName: "add" });
    }
  };

  // ------------------------------------------------- End => Action functions --------------------------------------------------------

  return (
    <>
      <SideDrawer
        isLoading={loading}
        headingText={t("glossary:finance-add-promo-code.title")}
        width={"648px"}
        openDrawer={OpenDrawer}
        close={close}
        anchorDrawer={"right"}
      >
        <form
          onSubmit={handleSubmit(() => {
            submitHandler();
          })}
        >
          <Stack rowGap={"15px"}>
            <TextInput
              fontSize={"15px"}
              label={t("glossary:finance-add-promo-code.code")}
              required={true}
              control={control}
              name={"promoCode"}
              placeholder={t(
                "glossary:finance-add-promo-code.placeholder.enter-promo-code",
              )}
              color={Theme.palette.background.pureWhite}
              width={"100%"}
              height={"33px"}
              maxLength={10}
            />

            {/* Provider Dropdown */}
            <RemoteSelect
              control={control}
              label={t("common:table-field.provider", {
                defaultValue: "Healthcare provider",
              })}
              placeholder="Select Healthcare provider"
              name={"providerId"}
              menuItemLabel={"providerName"}
              menuItemKey={"id"}
              options={providers}
              fullWidth={true}
              required={true}
              disabled={isUpdate}
              height={"50px"}
            />

            {/* Doctors — shown only when First Doctor is selected */}
            {isFirstDoctorSelected && (
              <RemoteSelectMultiple
                control={control}
                label={t("glossary:finance-add-promo-code.doctors")}
                placeholder={t(
                  "glossary:finance-add-promo-code.placeholder.select-doctors",
                )}
                name={"doctorIds"}
                menuItemLabel={"name"}
                menuItemKey={"id"}
                options={doctors}
                fullWidth={true}
                required={true}
                selectedValues={getValues().doctorIds}
                onSelectDeselectAll={handleOnSelectDeselectAll}
                height={"50px"}
              />
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: "16px",
              }}
            >
              <RemoteSelect
                control={control}
                label={t("common:table-field.promo-type")}
                placeholder={t(
                  "glossary:finance-add-promo-code.placeholder.select-promo-code-type",
                )}
                name={"promoCodeType"}
                menuItemLabel={"name"}
                menuItemKey={"id"}
                options={promoCodeTypes}
                fullWidth={false}
                required={true}
                height={"50px"}
              />

              <NumberInputType3
                required={true}
                label={t("common:table-field.value")}
                placeholder={t(
                  "glossary:finance-add-promo-code.placeholder.enter-value",
                )}
                control={control}
                name={"value"}
                size={"large"}
              />
            </div>

            <FormItemsWrap direction={"row"}>
              <div
                style={{
                  display: "flex",
                  gap: "40px",
                }}
              >
                <Calender
                  control={control}
                  name="dateRange"
                  required={true}
                  label={t("common:table-field.validity")}
                  placeholder={t("common:placeholder.select-validity-period")}
                  width={"258px"}
                />

                {/* Reusability — shown only when First Doctor is selected */}
                {isFirstDoctorSelected && (
                  <CheckBoxInput
                    name="reusability"
                    text={t("common:table-field.reusability")}
                    control={control}
                  />
                )}
              </div>
            </FormItemsWrap>

            <FormItemsWrap
              direction={"row"}
              justifyContent={"flex-end"}
              gap={"16px"}
            >
              <Button
                onClick={() => {
                  reset();
                  close();
                }}
                variant="LightBlueFontOnly"
                sx={{ maxHeight: "50px", fontWeight: "600" }}
              >
                {t("common:action.cancel")}
              </Button>

              <LoadingButtonType
                loading={LoadingState.visible}
                variant="primaryBlue"
                type="submit"
              >
                {isUpdate
                  ? t("common:action.update")
                  : t("common:action.add")}
              </LoadingButtonType>
            </FormItemsWrap>
          </Stack>
        </form>
      </SideDrawer>
    </>
  );
};
